import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  Avatar,
  Chip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { categoryAPI } from '../services/api';
import SkeletonLoader from '../components/common/SkeletonLoader';

interface CategoryMedia {
  id: string;
  categoryname: string;
  createdby: string;
  modifiedby: string | null;
  masterdetailId: string;
  categoryId: string;
  deletedAt: string | null;
  created: string;
  modified: string;
}

interface ProductMedia {
  id: string;
  productname: string;
  createdby: string;
  modifiedby: string | null;
  masterdetailId: string;
  productId: string;
  deletedAt: string | null;
  created: string;
  modified: string;
}

interface Category {
  id: string;
  name: string;
  categorystatus: number;
  totalproducts: number;
  totalsubcategories: number;
  createdby: string;
  modifiedby: string | null;
  parentId: string | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  groupcategoryId: string | null;
  masterdetailId: string;
  categorymedia: CategoryMedia[];
}

interface Product {
  id: string;
  name: string;
  productstatus: number;
  price: number; // This is the price/points value
  availablequantity: number; // This is the stock
  sellcounter: number; // This is the sold count
  categoryId: string;
  createdby: string;
  modifiedby: string | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  masterdetailId: string;
  productmedia: ProductMedia[];
}

const SubcategoryProductsList: React.FC = () => {
  const navigate = useNavigate();
  const { subcategoryId } = useParams<{ subcategoryId: string }>();
  
  // State for API data
  const [subcategoryData, setSubcategoryData] = useState<Category | null>(null);
  const [categoryData, setCategoryData] = useState<Category | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const loadCategoryData = useCallback(async (categoryId: string) => {
    try {
      const response = await categoryAPI.getCategoryDetails(categoryId);
      setCategoryData(response.data);
    } catch (error) {
      toast.error('Failed to load category data');
    }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      if (!subcategoryId) return;
      const response = await categoryAPI.getSubcategoryProducts(subcategoryId);
      
      // Handle different response structures
      if (Array.isArray(response.data)) {
        setProducts(response.data);
      } else if (response.data && Array.isArray(response.data.data)) {
        setProducts(response.data.data);
      } else if (response.data && Array.isArray(response.data.products)) {
        setProducts(response.data.products);
      } else {
        setProducts([]);
      }
    } catch (error) {
      toast.error('Failed to load products');
      setProducts([]);
    }
  }, [subcategoryId]);

  const loadSubcategoryData = useCallback(async () => {
    try {
      if (!subcategoryId) return;
      const response = await categoryAPI.getSubcategoryDetails(subcategoryId);
      setSubcategoryData(response.data);
      
      // After getting subcategory data, load category data
      if (response.data.parentId) {
        loadCategoryData(response.data.parentId);
      }
      
      // Load products for this subcategory
      loadProducts();
    } catch (error) {
      toast.error('Failed to load subcategory data');
    } finally {
      setLoading(false);
    }
  }, [subcategoryId, loadCategoryData, loadProducts]);

  // Load data when component mounts
  useEffect(() => {
    if (subcategoryId) {
      loadSubcategoryData();
    }
  }, [subcategoryId, loadSubcategoryData]);

  // const handleBackToSubcategories = () => {
  //   if (subcategoryData?.parentId) {
  //     navigate(`/subcategories/${subcategoryData.parentId}`);
  //   } else {
  //     navigate('/categories');
  //   }
  // };

  const handleBackToCategories = () => {
    navigate('/categories');
  };

  // Calculate statistics
  const activeProducts = products.filter(product => product.productstatus === 1).length;
  const inactiveProducts = products.filter(product => product.productstatus === 0).length;

  const stats = [
    { title: 'Total Products', value: products.length, color: 'primary' as const },
    { title: 'Active Products', value: activeProducts, color: 'success' as const },
    { title: 'Inactive Products', value: inactiveProducts, color: 'error' as const },
  ];

  return (
    <Box sx={{ px: 3, py: 2.5, backgroundColor: 'background.default', minHeight: '100vh' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={handleBackToCategories}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <BackIcon sx={{ mr: 0.5 }} />
          Categories
        </Link>
        {categoryData && (
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate(`/subcategories/${categoryData.id}`)}
          >
            {categoryData.name}
          </Link>
        )}
        <Typography color="text.primary">{subcategoryData?.name || ''}</Typography>
      </Breadcrumbs>

      {/* Header Card */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          p: 2,
          borderRadius: 3,
          background: '#fff',
          boxShadow: '0 4px 18px -4px rgba(0,0,0,0.12)',
          border: '1px solid',
          borderColor: 'grey.200'
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} letterSpacing={0.5}>
            Products {subcategoryData?.name ? `- ${subcategoryData.name}` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Browse products within this subcategory.
          </Typography>
        </Box>
      </Box>

      {/* Neutral Statistics Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, // 3 columns only, always large
          gap: 3.5,
          mb: 3,
        }}
      >
        {stats.map((stat, idx) => (
          <Card
            key={idx}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'grey.200',
              background: '#fff',
              position: 'relative',
              overflow: 'hidden',
              minHeight: 120,
              '&:before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background:
                  stat.color === 'primary'
                    ? 'linear-gradient(120deg,rgba(25,118,210,.06),transparent)'
                    : stat.color === 'success'
                    ? 'linear-gradient(120deg,rgba(46,125,50,.08),transparent)'
                    : 'linear-gradient(120deg,rgba(211,47,47,.08),transparent)',
                pointerEvents: 'none',
              },
            }}
          >
            <CardContent sx={{ py: 3.5, px: 3.5 }}>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.8,
                  color: `${stat.color}.main`,
                  fontSize: '1rem',
                }}
              >
                {stat.title}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, mt: 1.2 }}>
                {stat.value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Products Grid */}
      {loading ? (
        <SkeletonLoader type="productGrid" count={8} />
      ) : products.length === 0 ? (
         <Card>
           <CardContent>
             <Typography variant="h6" textAlign="center" color="text.secondary">
               No products found for this subcategory
             </Typography>
           </CardContent>
         </Card>
       ) : (
         <Box
           sx={{
             display: 'grid',
             gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
             gap: 3,
           }}
         >
           {products.map((product) => (
             <Card key={product.id} sx={{ 
               height: '100%', 
               display: 'flex', 
               flexDirection: 'column',
               border: '1px solid',
               borderColor: 'grey.200',
               borderRadius: 2,
               overflow: 'hidden',
               '&:hover': {
                 boxShadow: '0 4px 18px -4px rgba(0,0,0,0.18)',
                 transform: 'translateY(-2px)',
                 transition: 'all 0.28s ease'
               }
             }}>
               <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                 <Box sx={{ 
                   position: 'relative', 
                   width: '100%', 
                   paddingTop: '75%',
                   overflow: 'hidden',
                   backgroundColor: '#f5f5f5'
                 }}>
                   <Avatar
                     src={product.productmedia?.[0]?.productname ? categoryAPI.getMediaDownloadUrl(product.productmedia[0].productname) : undefined}
                     alt={product.name}
                     sx={{ 
                       width: '90%',
                       height: '90%',
                       position: 'absolute',
                       top: '5%',
                       left: '5%',
                       borderRadius: 1,
                       objectFit: 'cover',
                       border: '2px solid #ffffff',
                       boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                     }}
                   >
                     {product.name.charAt(0).toUpperCase()}
                   </Avatar>
                   <Chip
                     label={product.productstatus === 1 ? 'Active' : 'Inactive'}
                     color={product.productstatus === 1 ? 'success' : 'error'}
                     size="small"
                     sx={{ 
                       position: 'absolute', 
                       top: 12, 
                       right: 12,
                       fontSize: '0.65rem',
                       height: 22,
                       fontWeight: 600,
                       letterSpacing: .5
                     }}
                   />
                 </Box>
                 <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                   <Typography 
                     variant="body2"
                     fontWeight={600}
                     sx={{ 
                       mb: 2, 
                       textAlign: 'center',
                       color: 'text.secondary',
                       fontSize: '.95rem',
                       lineHeight: 1.3,
                       minHeight: '2.5rem',
                       display: '-webkit-box',
                       WebkitLineClamp: 2,
                       WebkitBoxOrient: 'vertical',
                       overflow: 'hidden'
                     }}
                   >
                     {product.name}
                   </Typography>
                   <Box sx={{ 
                     display: 'grid', 
                     gridTemplateColumns: '1fr 1fr 1fr', 
                     gap: 1,
                     mt: 'auto'
                   }}>
                     <Box sx={{ textAlign: 'center' }}>
                       <Typography variant="caption" color="text.secondary" sx={{ fontSize: '.65rem', letterSpacing: .5 }}>
                         Points
                       </Typography>
                       <Typography variant="body2" fontWeight={700} sx={{ fontSize: '.85rem', mt: .25, color: 'text.secondary' }}>
                         {product.price || 0}
                       </Typography>
                     </Box>
                     <Box sx={{ textAlign: 'center' }}>
                       <Typography variant="caption" color="text.secondary" sx={{ fontSize: '.65rem', letterSpacing: .5 }}>
                         Stock
                       </Typography>
                       <Typography variant="body2" fontWeight={700} sx={{ fontSize: '.85rem', mt: .25, color: 'text.secondary' }}>
                         {product.availablequantity || 0}
                       </Typography>
                     </Box>
                   </Box>
                 </Box>
               </CardContent>
             </Card>
           ))}
         </Box>
       )}
    </Box>
  );
};

export default SubcategoryProductsList;